import React, { useEffect, useMemo, useState } from "react";
import AdminLayout from "./AdminLayout";
import useNewsAdminServer from "./useNewsAdminServer";
import { fetchAdminHomeMainNotes, fetchAdminReplacementNotes, updateAdminHomeOrder } from "./api";

export default function FispTvAdminEditHome() {
    const { server, serverResolved } = useNewsAdminServer();
    const [originalItems, setOriginalItems] = useState([]);
    const [draftItems, setDraftItems] = useState([]);
    const [activeSlotIndex, setActiveSlotIndex] = useState(null);
    const [libraryItems, setLibraryItems] = useState([]);
    const [libraryPage, setLibraryPage] = useState(0);
    const [libraryHasMore, setLibraryHasMore] = useState(true);
    const [status, setStatus] = useState("loading");
    const [libraryStatus, setLibraryStatus] = useState("idle");
    const [saving, setSaving] = useState(false);
    const [feedback, setFeedback] = useState(null);

    useEffect(() => {
        if (!serverResolved) {
            return undefined;
        }

        const controller = new AbortController();
        setStatus("loading");
        setFeedback(null);

        fetchAdminHomeMainNotes(server, controller.signal)
            .then((items) => {
                setOriginalItems(items);
                setDraftItems(items);
                setStatus("ready");
            })
            .catch((error) => {
                if (error?.name === "AbortError") {
                    return;
                }

                setOriginalItems([]);
                setDraftItems([]);
                setStatus("error");
            });

        return () => controller.abort();
    }, [server, serverResolved]);

    useEffect(() => {
        if (activeSlotIndex === null || !serverResolved || libraryItems.length) {
            return undefined;
        }

        const controller = new AbortController();
        setLibraryStatus("loading");

        fetchAdminReplacementNotes(server, controller.signal, 1)
            .then((items) => {
                setLibraryItems(items);
                setLibraryPage(1);
                setLibraryHasMore(items.length >= 20);
                setLibraryStatus("ready");
            })
            .catch((error) => {
                if (error?.name === "AbortError") {
                    return;
                }

                setLibraryItems([]);
                setLibraryPage(0);
                setLibraryHasMore(false);
                setLibraryStatus("error");
            });

        return () => controller.abort();
    }, [activeSlotIndex, libraryItems.length, server, serverResolved]);

    const activeSlot = activeSlotIndex !== null ? draftItems[activeSlotIndex] || null : null;
    const nextPage = useMemo(() => libraryPage + 1, [libraryPage]);

    const loadMoreLibraryItems = async () => {
        if (!libraryHasMore || libraryStatus === "loading-more" || !serverResolved) {
            return;
        }

        setLibraryStatus("loading-more");

        try {
            const items = await fetchAdminReplacementNotes(server, undefined, nextPage);

            setLibraryItems((currentItems) => {
                const mergedItems = [...currentItems];
                const seenKeys = new Set(currentItems.map((item) => `${item.id}-${item.slug}`));

                items.forEach((item) => {
                    const itemKey = `${item.id}-${item.slug}`;

                    if (!seenKeys.has(itemKey)) {
                        seenKeys.add(itemKey);
                        mergedItems.push(item);
                    }
                });

                return mergedItems;
            });
            setLibraryPage(nextPage);
            setLibraryHasMore(items.length >= 20);
            setLibraryStatus("ready");
        } catch {
            setLibraryStatus("error");
        }
    };

    const handlePickReplacement = (candidate) => {
        if (activeSlotIndex === null || !activeSlot) {
            return;
        }

        const isUsedInAnotherSlot = draftItems.some((item, index) => index !== activeSlotIndex && item.id === candidate.id);

        if (isUsedInAnotherSlot) {
            setFeedback({ type: "error", message: "Esa nota ya está elegida en otra posición del home." });
            return;
        }

        setDraftItems((currentItems) =>
            currentItems.map((item, index) =>
                index === activeSlotIndex
                    ? {
                        ...candidate,
                        orderHome: item.orderHome,
                    }
                    : item
            )
        );
        setActiveSlotIndex(null);
        setFeedback(null);
    };

    const handleSave = async () => {
        if (!draftItems.length) {
            return;
        }

        setSaving(true);
        setFeedback(null);

        try {
            await Promise.all(
                originalItems.map((item) =>
                    updateAdminHomeOrder(
                        {
                            idnota: item.id,
                            presenteHome: 0,
                            ordenHome: 0,
                        },
                        server
                    )
                )
            );

            await Promise.all(
                [...draftItems]
                    .sort((leftItem, rightItem) => leftItem.orderHome - rightItem.orderHome)
                    .map((item) =>
                        updateAdminHomeOrder(
                            {
                                idnota: item.id,
                                presenteHome: 1,
                                ordenHome: item.orderHome,
                            },
                            server
                        )
                    )
            );

            const nextSnapshot = [...draftItems].sort((leftItem, rightItem) => leftItem.orderHome - rightItem.orderHome);
            setOriginalItems(nextSnapshot);
            setDraftItems(nextSnapshot);
            setFeedback({ type: "success", message: "La portada se actualizó correctamente." });
        } catch {
            setFeedback({ type: "error", message: "No pudimos guardar los cambios del home." });
        } finally {
            setSaving(false);
        }
    };

    return (
        <AdminLayout title="Editar home - Para el lunes lo subo" pageClassName="tv-admin-home-page">
            {/* {status === "loading" ? (
                <div className="tv-admin-fullscreen-spinner" aria-live="polite" aria-busy="true">
                    <span className="tv-admin-spinner" aria-hidden="true"></span>
                </div>
            ) : null}

            {status === "error" ? (
                <section className="tv-admin-placeholder-card is-home">
                    <h2>No pudimos cargar el home</h2>
                    <p>Hubo un problema al consultar las notas principales de la portada.</p>
                </section>
            ) : null}

            {status === "ready" ? (
                <>
                    {feedback ? <div className={`tv-admin-feedback is-${feedback.type}`}>{feedback.message}</div> : null}

                    <section className="tv-admin-home-stage">
                        <div className="tv-admin-home-grid">
                            {draftItems.map((item, index) => {
                                const isActive = index === activeSlotIndex;

                                return (
                                    <button
                                        key={`${item.id}-${item.orderHome}-${index}`}
                                        type="button"
                                        className={`tv-admin-home-card${isActive ? " is-active" : ""}`}
                                        onClick={() => {
                                            setActiveSlotIndex(index);
                                            setFeedback(null);
                                        }}
                                    >
                                        <span className="tv-admin-home-card-thumb">
                                            {item.image ? <img src={item.image} alt={item.title} /> : <span className="tv-admin-home-card-thumb-fallback"></span>}
                                        </span>
                                        <span className="tv-admin-home-card-copy">
                                            <small>Posición {item.orderHome}</small>
                                            <strong>{item.title}</strong>
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                    </section>

                    {activeSlot ? (
                        <div className="tv-admin-home-library-backdrop" onClick={() => setActiveSlotIndex(null)}>
                            <section className="tv-admin-home-library" onClick={(event) => event.stopPropagation()}>
                                <div className="tv-admin-home-library-head">
                                    <div>
                                        <h2>Reemplazar nota</h2>
                                        <p>{activeSlot.title}</p>
                                    </div>

                                    <button
                                        type="button"
                                        className="tv-admin-home-library-close"
                                        onClick={() => setActiveSlotIndex(null)}
                                    >
                                        Cerrar
                                    </button>
                                </div>

                                {libraryStatus === "loading" ? (
                                    <div className="tv-admin-fullscreen-spinner is-compact" aria-live="polite" aria-busy="true">
                                        <span className="tv-admin-spinner" aria-hidden="true"></span>
                                    </div>
                                ) : null}

                                {libraryStatus === "error" ? (
                                    <div className="tv-admin-feedback is-error">No pudimos cargar el listado de reemplazo.</div>
                                ) : null}

                                {libraryStatus !== "loading" && libraryItems.length ? (
                                    <>
                                        <div className="tv-admin-home-library-list">
                                            {libraryItems.map((item) => {
                                                const isCurrent = item.id === activeSlot.id;

                                                return (
                                                    <button
                                                        key={`${item.id}-${item.slug}`}
                                                        type="button"
                                                        className={`tv-admin-home-library-card${isCurrent ? " is-current" : ""}`}
                                                        onClick={() => handlePickReplacement(item)}
                                                    >
                                                        <span
                                                            className="tv-admin-home-library-category"
                                                            style={{ color: item.categoryColor || "#0a7e8c" }}
                                                        >
                                                            {item.category}
                                                        </span>
                                                        <span className="tv-admin-home-library-copy">
                                                            <strong>{item.title}</strong>
                                                        </span>
                                                    </button>
                                                );
                                            })}
                                        </div>

                                        {libraryHasMore ? (
                                            <div className="tv-admin-home-library-actions">
                                                <button
                                                    type="button"
                                                    className="tv-admin-secondary-btn"
                                                    onClick={loadMoreLibraryItems}
                                                    disabled={libraryStatus === "loading-more"}
                                                >
                                                    {libraryStatus === "loading-more" ? "Cargando..." : "Cargar más"}
                                                </button>
                                            </div>
                                        ) : null}
                                    </>
                                ) : null}
                            </section>
                        </div>
                    ) : null}

                    <div className="tv-admin-form-actions">
                        <button
                            type="button"
                            className="tv-admin-primary-btn"
                            onClick={handleSave}
                            disabled={saving || !draftItems.length}
                        >
                            {saving ? "Guardando..." : "Guardar home"}
                        </button>
                    </div>
                </>
            ) : null} */}
        </AdminLayout>
    );
}
